import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, ChevronRight, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeLabels = {
  7: 'Kansas History', 8: 'U.S. History', 9: 'World History',
  10: 'Modern World', 11: 'U.S. History', 12: 'Government & Economics'
};

export default function SkeletonNotesIndex() {
  const navigate = useNavigate();
  const [unitsByGrade, setUnitsByGrade] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeGrade, setActiveGrade] = useState(7);

  useEffect(() => {
    const load = async () => {
      const grades = [7, 8, 9, 10, 11, 12];
      const results = {};
      const fetches = await Promise.all(grades.map(g =>
        axios.get(`${API}/units/grade/${g}`).then(r => r.data).catch(() => [])
      ));
      grades.forEach((g, i) => { results[g] = fetches[i]; });
      setUnitsByGrade(results);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <AppShell>
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#14213D]" />
      </div>
    </AppShell>
  );

  const accent = gradeAccent(activeGrade);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
          <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight">Skeleton Notes</h1>
          <p className="text-sm text-[#5A6577] mt-2 max-w-2xl">Pre-populated student and teacher versions organized by grade level.</p>
        </div>

        {/* Grade tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="grade-tabs">
          {[7, 8, 9, 10, 11, 12].map(g => {
            const a = gradeAccent(g);
            const active = activeGrade === g;
            return (
              <button
                key={g}
                onClick={() => setActiveGrade(g)}
                className="text-xs px-4 py-2 rounded-md transition-colors border"
                style={active
                  ? { background: a.hex, color: '#fff', borderColor: a.hex }
                  : { background: 'white', color: '#2D3748', borderColor: '#EBE7E0' }}
                data-testid={`grade-tab-${g}`}
              >
                Grade {g}: {gradeLabels[g]}
              </button>
            );
          })}
        </div>

        {/* Units grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(unitsByGrade[activeGrade] || []).map((unit, i) => (
            <Card
              key={unit.id}
              className="group cursor-pointer border border-[#EBE7E0] shadow-sm hover:shadow-md hover:border-[#14213D]/20 transition-all bg-white overflow-hidden"
              onClick={() => navigate(`/skeleton-notes/${unit.id}`)}
              data-testid={`skeleton-note-card-${i}`}
            >
              <div className="h-1" style={{ background: accent.hex }} />
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: accent.hex }}>
                    Unit {unit.unit_number} &middot; {accent.subject}
                  </span>
                  <FileText className="h-4 w-4 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif-display text-base text-[#14213D] leading-snug mb-2 line-clamp-2">{unit.title}</h3>
                <p className="text-xs text-[#5A6577] line-clamp-2 mb-3 leading-relaxed">{unit.description?.slice(0, 120)}&hellip;</p>
                <div className="flex items-center text-xs font-medium text-[#14213D] group-hover:text-[#D4AF37] transition-colors">
                  <span>View notes</span>
                  <ChevronRight className="h-3 w-3 ml-1" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
